import { useState } from 'react';
import { useStore, selectCreature } from '@/infra/store';
import { useT } from '@/infra/i18n';
import { getWorldService } from '@/services/world';
import { dayPhaseFromMs } from '@/domain/world/dayphase';
import { currentBiome } from '@/domain/world/region';
import { VitalityBar } from './VitalityBar';
import { Minimap } from './Minimap';
import { InventoryModal } from './InventoryModal';
import { VirtualJoystick } from './VirtualJoystick';
import { EnterExpeditionButton } from './EnterExpeditionButton';
import { AfkDispatchModal } from './AfkDispatchModal';
import { AfkClaimModal } from './AfkClaimModal';
import { PixelIcon } from '@/presentation/icons/PixelIcon';

export function WorldHud() {
  const t = useT();
  const c = useStore(selectCreature);
  const playerX = useStore((s) => s.playerX);
  const playerY = useStore((s) => s.playerY);
  const [invOpen, setInvOpen] = useState(false);
  const [afkOpen, setAfkOpen] = useState(false);
  const [claimOpen, setClaimOpen] = useState(true);

  if (!c) return null;
  const biome = currentBiome(playerX, playerY);
  const phase = dayPhaseFromMs(Date.now());
  const phaseIcon = phase === 'day' ? 'sun' : 'moon';

  return (
    <>
      <div style={topLeft}>
        <VitalityBar />
        <div style={biomeBadge}>
          <PixelIcon name={phaseIcon} size={16} color="#ffd6b3" />
          <span>{t(`biome.${biome}`)}</span>
        </div>
      </div>

      <div style={topRight}>
        <Minimap />
        <button
          data-testid="btn-return-home"
          onClick={() => getWorldService().exitWorld(false)}
          style={returnBtn}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <PixelIcon name="house" size={16} color="#ffd6b3" />
            {t('world.exitToHouse')}
          </span>
        </button>
      </div>

      <button
        data-testid="btn-inventory"
        onClick={() => setInvOpen(true)}
        style={invBtn}
      >
        <PixelIcon name="inventory" size={32} color="#ffd6b3" />
      </button>

      <InventoryModal open={invOpen} onClose={() => setInvOpen(false)} />
      <AfkDispatchModal open={afkOpen} onClose={() => setAfkOpen(false)} />
      <AfkClaimModal open={claimOpen} onClose={() => setClaimOpen(false)} />
      <VirtualJoystick />
      <EnterExpeditionButton />
      <button
        data-testid="btn-afk-dispatch"
        onClick={() => setAfkOpen(true)}
        title="Despachar NPC"
        style={afkBtn}
      >
        <PixelIcon name="shield" size={32} color="#88aaff" />
      </button>
    </>
  );
}

const topLeft: React.CSSProperties = {
  position: 'fixed',
  top: 12,
  left: 12,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  zIndex: 90,
};

const biomeBadge: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  background: '#0e0e1c',
  padding: '4px 10px',
  border: '3px solid #ffd6b3',
  alignSelf: 'flex-start',
  fontFamily: '"Courier New", monospace',
  fontSize: 12,
  textTransform: 'uppercase',
  letterSpacing: 1,
  color: '#ffd6b3',
  boxShadow: '0 0 0 1px #0a0a14, 3px 3px 0 rgba(0,0,0,0.5)',
};

const topRight: React.CSSProperties = {
  position: 'fixed',
  top: 12,
  right: 12,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  alignItems: 'flex-end',
  zIndex: 90,
};

const returnBtn: React.CSSProperties = {
  background: '#1a1a2e',
  color: '#ffd6b3',
  border: '3px solid #ffd6b3',
  borderRadius: 0,
  padding: '8px 12px',
  cursor: 'pointer',
  fontSize: 12,
  fontFamily: '"Courier New", monospace',
  textTransform: 'uppercase',
  letterSpacing: 1,
  boxShadow: '0 0 0 1px #0a0a14, 3px 3px 0 rgba(255,214,179,0.3)',
  imageRendering: 'pixelated',
};

const invBtn: React.CSSProperties = {
  position: 'fixed',
  right: 16,
  bottom: 24,
  width: 60,
  height: 60,
  borderRadius: 0,
  background: '#1a1a2e',
  border: '3px solid #ffd6b3',
  fontSize: 28,
  cursor: 'pointer',
  zIndex: 95,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: '"Courier New", monospace',
  boxShadow: '0 0 0 1px #0a0a14, 3px 3px 0 rgba(255,214,179,0.3)',
};

const afkBtn: React.CSSProperties = {
  position: 'fixed',
  right: 84,
  bottom: 24,
  width: 60,
  height: 60,
  borderRadius: 0,
  background: '#1a1a2e',
  border: '3px solid #88aaff',
  fontSize: 24,
  cursor: 'pointer',
  zIndex: 95,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: '"Courier New", monospace',
  boxShadow: '0 0 0 1px #0a0a14, 3px 3px 0 rgba(136,170,255,0.3)',
};
